/**
 * Line Shares on the client (RP-24) — the Share set a grid action sends.
 *
 * `PUT /api/plan-lines/:lineId/shares` replaces a line's WHOLE Share set, so every action in
 * the grid's subtree — add a Resource, remove one, save typed hours or points — builds the
 * full set from what the line holds now plus the one change. Pure, so the arithmetic of
 * "what does adding Bo send" is testable without a browser.
 *
 * Hours travel in `YYYY-MM` months, as everywhere on the plan API.
 */

import type { PlanLineView } from "@/schemas/resource-plans";

export interface PlanLineShareInput {
  resourceId: string;
  points: number;
  months?: Array<{ month: string; hours: string }>;
  steadyStateHours?: string | null;
}

/** The Supporting default — what a newly added Share's points start at, for the RM to edit. */
export const DEFAULT_SHARE_POINTS = 1;

type LineForShares = Pick<
  PlanLineView,
  "suggestedResourceId" | "months" | "steadyStateHours" | "shares"
>;

function nonEmpty(months: ReadonlyArray<{ month: string; hours: string }>) {
  return months.filter((c) => c.hours !== "" && Number(c.hours) > 0);
}

/** The line's saved Shares, as the PUT takes them. Empty on an unsplit line. */
export function savedShareInputs(line: LineForShares, steadyState: boolean): PlanLineShareInput[] {
  return (line.shares ?? []).map((s) =>
    steadyState
      ? {
          resourceId: s.resourceId,
          points: s.points ?? DEFAULT_SHARE_POINTS,
          steadyStateHours: s.steadyStateHours,
        }
      : {
          resourceId: s.resourceId,
          points: s.points ?? DEFAULT_SHARE_POINTS,
          months: nonEmpty(s.months),
        },
  );
}

/**
 * Add a Resource to a line. On an unsplit line naming someone, this IS the split: the
 * current Resource keeps every hour and the new one starts at none (so nothing is
 * unallocated), and the RM then moves hours across. On a split line, the new Share starts
 * empty. Returns null when there is nothing to split from — a line naming nobody takes its
 * first Resource as its Suggested Resource, not as a Share.
 */
export function sharesAfterAdding(
  line: LineForShares,
  resourceId: string,
  steadyState: boolean,
): PlanLineShareInput[] | null {
  const empty: PlanLineShareInput = steadyState
    ? { resourceId, points: DEFAULT_SHARE_POINTS, steadyStateHours: null }
    : { resourceId, points: DEFAULT_SHARE_POINTS, months: [] };
  const saved = savedShareInputs(line, steadyState);
  if (saved.length > 0) {
    if (saved.some((s) => s.resourceId === resourceId)) return null;
    return [...saved, empty];
  }
  const current = line.suggestedResourceId;
  if (current === null || current === resourceId) return null;
  const whole: PlanLineShareInput = steadyState
    ? { resourceId: current, points: DEFAULT_SHARE_POINTS, steadyStateHours: line.steadyStateHours }
    : { resourceId: current, points: DEFAULT_SHARE_POINTS, months: nonEmpty(line.months) };
  return [whole, empty];
}

/** Remove a Resource's Share. Down to one, the server makes them the Suggested Resource. */
export function sharesAfterRemoving(
  line: LineForShares,
  resourceId: string,
  steadyState: boolean,
): PlanLineShareInput[] {
  return savedShareInputs(line, steadyState).filter((s) => s.resourceId !== resourceId);
}

/**
 * The set to send after typing in the subtree: each saved Share with its hours and points
 * read from the grid's draft. `readHours(resourceId, month)` / `readRunRate(resourceId)` /
 * `readPoints(resourceId)` return the draft value, or the saved one when untouched.
 */
export function sharesFromDraft(input: {
  line: LineForShares;
  months: readonly string[];
  steadyState: boolean;
  readHours: (resourceId: string, month: string) => string;
  readRunRate: (resourceId: string) => string;
  readPoints: (resourceId: string) => number;
}): PlanLineShareInput[] {
  return (input.line.shares ?? []).map((s) => {
    const points = input.readPoints(s.resourceId);
    if (input.steadyState) {
      const rate = input.readRunRate(s.resourceId);
      return { resourceId: s.resourceId, points, steadyStateHours: rate === "" ? null : rate };
    }
    return {
      resourceId: s.resourceId,
      points,
      months: nonEmpty(
        input.months.map((month) => ({ month, hours: input.readHours(s.resourceId, month) })),
      ),
    };
  });
}
